import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Tooltip,
  Legend,
  ChartEvent,
  ActiveElement
} from 'chart.js';
import { Bar, Line, Pie } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { useAuth } from '../contexts/AuthContext';
import { dashboardAPI, departmentsAPI, vendorsAPI, currenciesAPI } from '../services/api';
import {
  VendorAnalyticsData,
  VendorAnalyticsDailyData,
  VendorAnalyticsResponse,
  VendorAnalyticsDailyResponse,
  Department,
  Vendor,
  Currency
} from '../types';
import { AlertCircle, RefreshCw, SlidersHorizontal, X } from 'lucide-react';
import { getCurrencyFontClass } from '../utils/currency';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Tooltip,
  Legend,
  ChartDataLabels
);

const PRIMARY_CHART_COLOR = '#424ee6';
const palette = [
  PRIMARY_CHART_COLOR,
  '#f97316',
  '#22c55e',
  '#a855f7',
  '#ef4444',
  '#14b8a6',
  '#facc15',
  '#6366f1',
  '#ec4899',
  '#0ea5e9'
];

const formatCurrency = (value: number, currency: string = 'USD') =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0
  }).format(value);

const FILTER_STORAGE_KEY = 'vendor-analytics-filters';
const DEFAULT_MAX_MONTHS = 10;
const AGE_BRACKETS = ['0-30', '31-60', '>60'] as const;

const STATUS_OPTIONS: Array<{ label: string; value: string }> = [
  { label: 'Pending', value: 'pending' },
  { label: 'Finance Team Approved', value: 'level_1_approved' },
  { label: 'Level 2 Approved', value: 'level_2_approved' },
  { label: 'Level 3 Approved', value: 'level_3_approved' },
  { label: 'Paid', value: 'paid' },
  { label: 'Rejected', value: 'rejected' },
  { label: 'Overdue', value: 'overdue' }
];

type FilterChip = {
  key: string;
  label: string;
  onRemove: () => void;
};

type FilterState = {
  startDate: string | null;
  endDate: string | null;
  departmentIds: number[];
  vendorIds: number[];
  categories: string[];
  statuses: string[];
  currencies: string[];
  minAmount: string;
  maxAmount: string;
  ageBrackets: string[];
  maxMonths: number;
};

const createEmptyFilters = (): FilterState => ({
  startDate: null,
  endDate: null,
  departmentIds: [],
  vendorIds: [],
  categories: [],
  statuses: [],
  currencies: [],
  minAmount: '',
  maxAmount: '',
  ageBrackets: [],
  maxMonths: DEFAULT_MAX_MONTHS
});

const clampMonths = (value?: number | null): number => {
  if (value === undefined || value === null || Number.isNaN(Number(value))) {
    return DEFAULT_MAX_MONTHS;
  }
  const coerced = Math.trunc(Number(value));
  return Math.min(Math.max(coerced, 1), 36);
};

const getNextIsoDate = (isoDate: string): string => {
  const [year, month, day] = isoDate.split('-').map(Number);
  if ([year, month, day].some((value) => Number.isNaN(value))) {
    return isoDate;
  }
  const date = new Date(Date.UTC(year, (month ?? 1) - 1, day ?? 1));
  date.setUTCDate(date.getUTCDate() + 1);
  return date.toISOString().slice(0, 10);
};

const sanitizeFilters = (filters: FilterState): FilterState => {
  const uniqueNumbers = (values: Array<number | string>): number[] =>
    Array.from(
      new Set(
        values
          .map((value) => Number(value))
          .filter((value) => Number.isFinite(value))
      )
    );

  const uniqueStrings = (values: string[]): string[] =>
    Array.from(
      new Set(
        values
          .map((value) => value.trim())
          .filter((value) => value.length > 0)
      )
    );

  return {
    startDate: filters.startDate && filters.startDate.length === 10 ? filters.startDate : null,
    endDate: filters.endDate && filters.endDate.length === 10 ? filters.endDate : null,
    departmentIds: uniqueNumbers(filters.departmentIds),
    vendorIds: uniqueNumbers(filters.vendorIds),
    categories: uniqueStrings(filters.categories),
    statuses: uniqueStrings(filters.statuses),
    currencies: uniqueStrings(filters.currencies).map((value) => value.toUpperCase()),
    minAmount: filters.minAmount.trim(),
    maxAmount: filters.maxAmount.trim(),
    ageBrackets: uniqueStrings(filters.ageBrackets).filter((bracket) =>
      AGE_BRACKETS.includes(bracket as (typeof AGE_BRACKETS)[number])
    ),
    maxMonths: clampMonths(filters.maxMonths)
  };
};

const normalizeForCompare = (filters: FilterState) => ({
  ...filters,
  departmentIds: [...filters.departmentIds].sort((a, b) => a - b),
  vendorIds: [...filters.vendorIds].sort((a, b) => a - b),
  categories: [...filters.categories].sort(),
  statuses: [...filters.statuses].sort(),
  currencies: [...filters.currencies].sort(),
  ageBrackets: [...filters.ageBrackets].sort(),
  minAmount: filters.minAmount.trim(),
  maxAmount: filters.maxAmount.trim(),
  startDate: filters.startDate || null,
  endDate: filters.endDate || null,
  maxMonths: clampMonths(filters.maxMonths)
});

const areFiltersEqual = (a: FilterState, b: FilterState) =>
  JSON.stringify(normalizeForCompare(a)) === JSON.stringify(normalizeForCompare(b));

const buildQueryParams = (filters: FilterState): Record<string, string> => {
  const params: Record<string, string> = {};

  if (filters.startDate) params.startDate = filters.startDate;
  if (filters.endDate) params.endDate = filters.endDate;
  if (filters.departmentIds.length) params.departmentIds = filters.departmentIds.join(',');
  if (filters.vendorIds.length) params.vendorIds = filters.vendorIds.join(',');
  if (filters.categories.length) params.expenseCategories = filters.categories.join(',');
  if (filters.statuses.length) params.statuses = filters.statuses.join(',');
  if (filters.currencies.length) params.currencies = filters.currencies.join(',');

  const minAmount = filters.minAmount ? Number(filters.minAmount) : null;
  if (minAmount !== null && !Number.isNaN(minAmount)) params.minAmount = String(minAmount);
  const maxAmount = filters.maxAmount ? Number(filters.maxAmount) : null;
  if (maxAmount !== null && !Number.isNaN(maxAmount)) params.maxAmount = String(maxAmount);

  if (filters.ageBrackets.length) params.ageBrackets = filters.ageBrackets.join(',');
  if (filters.maxMonths !== DEFAULT_MAX_MONTHS) params.maxMonths = String(filters.maxMonths);

  return params;
};

const updateUrlWithFilters = (filters: FilterState) => {
  if (typeof window === 'undefined') return;
  const params = buildQueryParams(filters);
  const searchParams = new URLSearchParams(params);
  const newUrl = `${window.location.pathname}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
  window.history.replaceState({}, '', newUrl);
};

const loadFiltersFromQuery = (search: string): FilterState | null => {
  if (!search || search.length <= 1) return null;
  const params = new URLSearchParams(search);
  const filters = createEmptyFilters();

  const startDate = params.get('startDate');
  if (startDate) filters.startDate = startDate;
  const endDate = params.get('endDate');
  if (endDate) filters.endDate = endDate;

  const departmentIds = params.get('departmentIds');
  if (departmentIds) {
    filters.departmentIds = departmentIds
      .split(',')
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value));
  }

  const vendorIds = params.get('vendorIds');
  if (vendorIds) {
    filters.vendorIds = vendorIds
      .split(',')
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value));
  }

  const categories = params.get('expenseCategories');
  if (categories) {
    filters.categories = categories.split(',').map((value) => decodeURIComponent(value));
  }

  const statuses = params.get('statuses');
  if (statuses) {
    filters.statuses = statuses.split(',').map((value) => decodeURIComponent(value));
  }

  const currencies = params.get('currencies');
  if (currencies) {
    filters.currencies = currencies
      .split(',')
      .map((value) => decodeURIComponent(value))
      .map((value) => value.toUpperCase());
  }

  const minAmount = params.get('minAmount');
  if (minAmount) filters.minAmount = minAmount;
  const maxAmount = params.get('maxAmount');
  if (maxAmount) filters.maxAmount = maxAmount;

  const ageBrackets = params.get('ageBrackets');
  if (ageBrackets) {
    filters.ageBrackets = ageBrackets.split(',').map((value) => decodeURIComponent(value));
  }

  const maxMonths = params.get('maxMonths');
  if (maxMonths) filters.maxMonths = clampMonths(Number(maxMonths));

  return sanitizeFilters(filters);
};

const loadFiltersFromStorage = (): FilterState | null => {
  if (typeof window === 'undefined') return null;
  const raw = window.localStorage.getItem(FILTER_STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return sanitizeFilters({
      ...createEmptyFilters(),
      ...parsed
    });
  } catch (error) {
    console.warn('Failed to parse stored vendor analytics filters', error);
    return null;
  }
};

const persistFilters = (filters: FilterState) => {
  if (typeof window === 'undefined') return;
  const sanitized = sanitizeFilters(filters);
  window.localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(sanitized));
  updateUrlWithFilters(sanitized);
};

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const appliedFiltersRef = useRef<FilterState>(createEmptyFilters());
  const [appliedFilters, setAppliedFilters] = useState<FilterState>(createEmptyFilters());
  const [draftFilters, setDraftFilters] = useState<FilterState>(createEmptyFilters());
  const [analytics, setAnalytics] = useState<VendorAnalyticsData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [currencyOptions, setCurrencyOptions] = useState<Currency[]>([]);
  const [optionsLoading, setOptionsLoading] = useState<boolean>(false);
  const [departmentSearch, setDepartmentSearch] = useState('');
  const [vendorSearch, setVendorSearch] = useState('');
  const [currencySearch, setCurrencySearch] = useState('');

  const [dailyData, setDailyData] = useState<VendorAnalyticsDailyData | null>(null);
  const [dailyLoading, setDailyLoading] = useState<boolean>(false);
  const [dailyError, setDailyError] = useState<string | null>(null);
  const [dailyModalOpen, setDailyModalOpen] = useState<boolean>(false);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const fetchAnalytics = useCallback(
    async (filtersOverride?: FilterState) => {
      const filtersToUse = sanitizeFilters(filtersOverride ?? appliedFiltersRef.current);
      try {
        setLoading(true);
        setError(null);
        const params = buildQueryParams(filtersToUse);
        const response = (await dashboardAPI.getVendorAnalytics(params)) as VendorAnalyticsResponse;
        if (!response?.success) {
          throw new Error(response?.message || 'Unable to load vendor analytics.');
        }
        setAnalytics(response.data);
      } catch (err) {
        console.error('Error fetching vendor analytics:', err);
        setError(err instanceof Error ? err.message : 'Failed to load vendor analytics.');
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const updateAppliedFilters = useCallback(
    (
      nextFilters: FilterState,
      options?: { closePanel?: boolean; suppressFetch?: boolean }
    ) => {
      const sanitized = sanitizeFilters(nextFilters);
      setAppliedFilters(sanitized);
      appliedFiltersRef.current = sanitized;
      setDraftFilters(sanitized);
      persistFilters(sanitized);
      if (options?.closePanel) {
        setIsFilterPanelOpen(false);
        setDepartmentSearch('');
        setVendorSearch('');
        setCurrencySearch('');
      }
      if (!options?.suppressFetch) {
        void fetchAnalytics(sanitized);
      }
    },
    [fetchAnalytics]
  );

  const initializeFilters = useCallback(() => {
    const queryFilters =
      typeof window !== 'undefined' ? loadFiltersFromQuery(window.location.search) : null;
    const storedFilters = loadFiltersFromStorage();
    const initial = sanitizeFilters(queryFilters ?? storedFilters ?? createEmptyFilters());
    setAppliedFilters(initial);
    appliedFiltersRef.current = initial;
    setDraftFilters(initial);
    persistFilters(initial);
    filtersInitializedRef.current = true;
  }, []);

  useEffect(() => {
    initializeFilters();
  }, [initializeFilters]);

  const filtersInitializedRef = useRef(false);

  useEffect(() => {
    const loadOptions = async () => {
      let defaultCurrencyCode: string | null = null;
      try {
        setOptionsLoading(true);
        const [departmentResponse, vendorResponse, currencyResponse] = await Promise.all([
          departmentsAPI.dropDown(),
          vendorsAPI.allVendorsDropdown(),
          currenciesAPI.dropDown()
        ]);

        if (departmentResponse?.success && Array.isArray(departmentResponse.data)) {
          setDepartments(departmentResponse.data);
        }
        if (vendorResponse?.success && Array.isArray(vendorResponse.data)) {
          setVendors(vendorResponse.data);
        }
        if (currencyResponse?.success && Array.isArray(currencyResponse.data)) {
          const currencyList = currencyResponse.data as Currency[];
          setCurrencyOptions(currencyList);
          const preferred =
            currencyList.find((currency) => currency.is_default)?.name ||
            currencyList[0]?.name;
          if (preferred) {
            defaultCurrencyCode = preferred.toUpperCase();
          }
        }
      } catch (err) {
        console.error('Error loading filter options:', err);
      } finally {
        setOptionsLoading(false);
        if (filtersInitializedRef.current) {
          const currentFilters = appliedFiltersRef.current;
          let triggeredFetch = false;
          if (defaultCurrencyCode && currentFilters.currencies.length === 0) {
            updateAppliedFilters(
              {
                ...currentFilters,
                currencies: [defaultCurrencyCode]
              },
              { suppressFetch: false }
            );
            triggeredFetch = true;
          }
          if (!triggeredFetch) {
            void fetchAnalytics(currentFilters);
          }
        }
      }
    };

    void loadOptions();
  }, [fetchAnalytics, updateAppliedFilters]);

  const handleOpenFilterPanel = useCallback(() => {
    setDraftFilters(appliedFilters);
    setDepartmentSearch('');
    setVendorSearch('');
    setCurrencySearch('');
    setIsFilterPanelOpen(true);
  }, [appliedFilters]);

  const handleResetDraftFilters = useCallback(() => {
    setDraftFilters(createEmptyFilters());
    setDepartmentSearch('');
    setVendorSearch('');
    setCurrencySearch('');
  }, []);

  const handleApplyFilters = useCallback(() => {
    updateAppliedFilters(draftFilters, { closePanel: true });
  }, [draftFilters, updateAppliedFilters]);

  const handleRemoveFilter = useCallback(
    (transform: (current: FilterState) => FilterState) => {
      updateAppliedFilters(transform(appliedFilters));
    },
    [appliedFilters, updateAppliedFilters]
  );

  const clearAllFilters = useCallback(() => {
    updateAppliedFilters(createEmptyFilters());
    setDepartmentSearch('');
    setVendorSearch('');
    setCurrencySearch('');
  }, [updateAppliedFilters]);

  const filtersChanged = useMemo(
    () => !areFiltersEqual(draftFilters, appliedFilters),
    [draftFilters, appliedFilters]
  );

  const filteredDepartments = useMemo(() => {
    const query = departmentSearch.trim().toLowerCase();
    if (!query) return departments;
    return departments.filter((dept) => {
      const name = dept.department?.toLowerCase() ?? '';
      const code = (dept.department_code as string | undefined)?.toLowerCase() ?? '';
      return name.includes(query) || code.includes(query);
    });
  }, [departments, departmentSearch]);

  const filteredVendors = useMemo(() => {
    const query = vendorSearch.trim().toLowerCase();
    if (!query) return vendors;
    return vendors.filter((vendor) => {
      const name = vendor.name?.toLowerCase() ?? (vendor as any)?.vendor_name?.toLowerCase() ?? '';
      return name.includes(query);
    });
  }, [vendors, vendorSearch]);

  const filteredCurrencies = useMemo(() => {
    const query = currencySearch.trim().toLowerCase();
    if (!query) return currencyOptions;
    return currencyOptions.filter((currency) =>
      currency.name?.toLowerCase().includes(query)
    );
  }, [currencyOptions, currencySearch]);

  const primaryCurrency = useMemo(() => {
    if (appliedFilters.currencies.length === 1) {
      return appliedFilters.currencies[0]?.toUpperCase?.() ?? 'USD';
    }
    return 'USD';
  }, [appliedFilters.currencies]);

  const dailyCurrency = useMemo(() => {
    const summaryCurrency =
      (dailyData?.summary as { currency?: string } | undefined)?.currency;
    if (summaryCurrency && typeof summaryCurrency === 'string') {
      return summaryCurrency.toUpperCase();
    }
    return primaryCurrency;
  }, [dailyData, primaryCurrency]);
  const dailyCurrencyClass = useMemo(
    () => getCurrencyFontClass(dailyCurrency),
    [dailyCurrency]
  );

  const getDepartmentLabel = useCallback(
    (id: number) => {
      const match = departments.find((dept) => Number(dept.id) === Number(id));
      return match?.department || match?.department_code || `Department #${id}`;
    },
    [departments]
  );

  const getVendorLabel = useCallback(
    (id: number) => {
      const match = vendors.find((vendor) => Number(vendor.id) === Number(id));
      return match?.name || (match as any)?.vendor_name || `Vendor #${id}`;
    },
    [vendors]
  );

  const getStatusLabel = useCallback((value: string) => {
    const match = STATUS_OPTIONS.find((option) => option.value === value);
    return match ? match.label : value.replace(/_/g, ' ');
  }, []);

  const activeFilterChips = useMemo<FilterChip[]>(() => {
    const chips: FilterChip[] = [];

    if (appliedFilters.startDate || appliedFilters.endDate) {
      if (appliedFilters.startDate && appliedFilters.endDate) {
        chips.push({
          key: 'date-range',
          label: `Dates: ${appliedFilters.startDate} → ${appliedFilters.endDate}`,
          onRemove: () =>
            handleRemoveFilter((current) => ({ ...current, startDate: null, endDate: null }))
        });
      } else if (appliedFilters.startDate) {
        chips.push({
          key: 'start-date',
          label: `From: ${appliedFilters.startDate}`,
          onRemove: () => handleRemoveFilter((current) => ({ ...current, startDate: null }))
        });
      } else if (appliedFilters.endDate) {
        chips.push({
          key: 'end-date',
          label: `Until: ${appliedFilters.endDate}`,
          onRemove: () => handleRemoveFilter((current) => ({ ...current, endDate: null }))
        });
      }
    }

    appliedFilters.departmentIds.forEach((id) => {
      chips.push({
        key: `department-${id}`,
        label: `Department: ${getDepartmentLabel(id)}`,
        onRemove: () =>
          handleRemoveFilter((current) => ({
            ...current,
            departmentIds: current.departmentIds.filter((deptId) => deptId !== id)
          }))
      });
    });

    appliedFilters.vendorIds.forEach((id) => {
      chips.push({
        key: `vendor-${id}`,
        label: `Vendor: ${getVendorLabel(id)}`,
        onRemove: () =>
          handleRemoveFilter((current) => ({
            ...current,
            vendorIds: current.vendorIds.filter((vendorId) => vendorId !== id)
          }))
      });
    });

    appliedFilters.categories.forEach((category) => {
      chips.push({
        key: `category-${category}`,
        label: `Category: ${category}`,
        onRemove: () =>
          handleRemoveFilter((current) => ({
            ...current,
            categories: current.categories.filter((item) => item !== category)
          }))
      });
    });

    appliedFilters.statuses.forEach((status) => {
      chips.push({
        key: `status-${status}`,
        label: `Status: ${getStatusLabel(status)}`,
        onRemove: () =>
          handleRemoveFilter((current) => ({
            ...current,
            statuses: current.statuses.filter((item) => item !== status)
          }))
      });
    });

    appliedFilters.currencies.forEach((currency) => {
      const normalized = currency.toUpperCase();
      chips.push({
        key: `currency-${normalized}`,
        label: `Currency: ${normalized}`,
        onRemove: () =>
          handleRemoveFilter((current) => ({
            ...current,
            currencies: current.currencies.filter((item) => item !== normalized)
          }))
      });
    });

    if (appliedFilters.minAmount || appliedFilters.maxAmount) {
      const parts: string[] = [];
      if (appliedFilters.minAmount) {
        parts.push(`≥ ${Number(appliedFilters.minAmount).toLocaleString()}`);
      }
      if (appliedFilters.maxAmount) {
        parts.push(`≤ ${Number(appliedFilters.maxAmount).toLocaleString()}`);
      }
      chips.push({
        key: 'amount-range',
        label: `Amount ${parts.join(' & ')}`,
        onRemove: () =>
          handleRemoveFilter((current) => ({
            ...current,
            minAmount: '',
            maxAmount: ''
          }))
      });
    }

    appliedFilters.ageBrackets.forEach((bracket) => {
      chips.push({
        key: `age-${bracket}`,
        label: `Aging: ${bracket}`,
        onRemove: () =>
          handleRemoveFilter((current) => ({
            ...current,
            ageBrackets: current.ageBrackets.filter((item) => item !== bracket)
          }))
      });
    });

    if (appliedFilters.maxMonths !== DEFAULT_MAX_MONTHS) {
      chips.push({
        key: 'max-months',
        label: `Months: ${appliedFilters.maxMonths}`,
        onRemove: () =>
          handleRemoveFilter((current) => ({
            ...current,
            maxMonths: DEFAULT_MAX_MONTHS
          }))
      });
    }

    return chips;
  }, [
    appliedFilters,
    getDepartmentLabel,
    getVendorLabel,
    getStatusLabel,
    handleRemoveFilter
  ]);

  const openDailyModal = async (date: string) => {
    setSelectedDate(date);
    setDailyModalOpen(true);
    setDailyLoading(true);
    setDailyError(null);

    try {
      const filterParams = buildQueryParams(appliedFiltersRef.current);
      const response = (await dashboardAPI.getVendorAnalyticsDaily({
        date,
        ...filterParams
      })) as VendorAnalyticsDailyResponse;
      if (!response?.success) {
        throw new Error(response?.message || 'Unable to load daily analytics.');
      }
      const payload = response.data;
      setDailyData({
        ...payload,
        statusBreakdown: payload.statusBreakdown ?? [],
        statusChanges: payload.statusChanges ?? [],
        invoices: payload.invoices ?? []
      });
    } catch (err) {
      console.error('Error fetching daily analytics:', err);
      setDailyError(err instanceof Error ? err.message : 'Failed to load daily analytics.');
      setDailyData(null);
    } finally {
      setDailyLoading(false);
    }
  };

  const handleDailyBarClick = (_event: ChartEvent, elements: ActiveElement[]) => {
    if (!analytics || !elements?.length) return;
    const element = elements[0];
    const index = element.index;
    const targetDay = analytics.lastSevenDays[index];
    if (targetDay) {
      void openDailyModal(targetDay.date);
    }
  };

  const departmentSpendData = useMemo(() => {
    if (!analytics?.departmentSpend?.length) return null;

    const labels = analytics.departmentSpend.map((item) => item.department_code || 'Unassigned');
    const amounts = analytics.departmentSpend.map((item) => Number(item.total_spend || 0));

    return {
      labels,
      datasets: [
        {
          label: 'Total Spend',
          data: amounts,
          backgroundColor: PRIMARY_CHART_COLOR
        }
      ]
    };
  }, [analytics]);

  const vendorExpenseData = useMemo(() => {
    if (!analytics?.vendorExpenses?.length) return null;
    const labels = analytics.vendorExpenses.map((item) => item.vendor_name || 'Unknown');
    const dataset = analytics.vendorExpenses.map((item) => Number(item.total_spend || 0));

    return {
      labels,
      datasets: [
        {
          label: 'Total Spend',
          data: dataset,
          backgroundColor: labels.map((_, idx) => palette[idx % palette.length])
        }
      ]
    };
  }, [analytics]);

  const monthlyTrendData = useMemo(() => {
    if (!analytics?.monthlyTrend?.length) return null;
    const labels = analytics.monthlyTrend.map((item) => item.month);
    const dataset = analytics.monthlyTrend.map((item) => Number(item.total_spend || 0));

    return {
      labels,
      datasets: [
        {
          label: 'Monthly Spend',
          data: dataset,
          borderColor: PRIMARY_CHART_COLOR,
          backgroundColor: 'rgba(15, 92, 127, 0.15)',
          tension: 0.3,
          fill: true,
          pointBackgroundColor: PRIMARY_CHART_COLOR
        }
      ]
    };
  }, [analytics]);

  const lastSevenDaysData = useMemo(() => {
    if (!analytics?.lastSevenDays?.length) return null;
    const labels = analytics.lastSevenDays.map((day) => day.date);
    const amounts = analytics.lastSevenDays.map((day) => Number(day.totalAmount || 0));

    return {
      labels,
      datasets: [
        {
          label: 'Total Amount',
          data: amounts,
          backgroundColor: PRIMARY_CHART_COLOR
        }
      ]
    };
  }, [analytics]);

  const isInitialLoad = loading && !analytics;
  const showInlineError = error && analytics;

  if (isInitialLoad) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (error && !analytics) {
    return (
      <div className="rounded-lg bg-red-50 p-4 border border-red-200">
        <div className="flex">
          <div className="flex-shrink-0">
            <AlertCircle className="h-5 w-5 text-red-400" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error loading dashboard</h3>
            <div className="mt-2 text-sm text-red-700">{error}</div>
            <button
              onClick={() => void fetchAnalytics()}
              className="mt-4 inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full px-4 py-6 space-y-6">
      {loading && analytics && (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/70">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
        </div>
      )}

      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Vendor Analytics Dashboard</h1>
          <p className="mt-1 text-sm text-gray-500">
            Welcome back, {user?.first_name}. Here’s how your vendor spend is trending.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={handleOpenFilterPanel}
            className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
          >
            <SlidersHorizontal className="h-4 w-4 mr-2" />
            Filters
          </button>
          <button
            onClick={() => void fetchAnalytics()}
            className="inline-flex items-center px-3 py-2 text-sm font-medium text-primary-600 border border-primary-200 rounded-md hover:bg-primary-50 transition-colors"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {activeFilterChips.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          {activeFilterChips.map((chip) => (
            <span
              key={chip.key}
              className="inline-flex items-center gap-2 rounded-full bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700"
            >
              {chip.label}
              <button
                onClick={chip.onRemove}
                className="text-primary-500 transition-colors hover:text-primary-700"
                aria-label={`Remove ${chip.label}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          <button
            onClick={clearAllFilters}
            className="text-xs font-medium text-gray-500 underline decoration-dotted underline-offset-4 hover:text-gray-700"
          >
            Clear all
          </button>
        </div>
      )}

      {showInlineError && (
        <div className="rounded-lg bg-red-50 p-4 border border-red-200">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">We hit a snag fetching analytics</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
              <button
                onClick={() => void fetchAnalytics()}
                className="mt-4 inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold text-gray-900">Department Spend</h3>
          </div>
          <div className="card-body">
            {departmentSpendData ? (
              <div className="h-64">
                <Bar
                  data={departmentSpendData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: false },
                      datalabels: {
                        anchor: 'center',
                        align: 'center',
                        color: '#ffffff',
                        font: {
                          weight: 'bold',
                          size: 13
                        },
                        formatter: (value: number) => {
                          // Format with decimals if needed
                          return new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: primaryCurrency,
                            minimumFractionDigits: value % 1 !== 0 ? 2 : 0,
                            maximumFractionDigits: 2
                          }).format(value);
                        },
                        display: () => {
                          // Always show labels
                          return true;
                        }
                      }
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        ticks: {
                          callback: (value) => formatCurrency(Number(value), primaryCurrency)
                        }
                      }
                    }
                  }}
                />
              </div>
            ) : (
              <EmptyState message="No department spend data available yet." />
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold text-gray-900">Vendor Wise Expenses</h3>
          </div>
          <div className="card-body">
            {vendorExpenseData ? (
              <div className="h-64">
                <Pie
                  data={vendorExpenseData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'right'
                      },
                      datalabels: {
                        display: true,
                        color: '#ffffff',
                        font: {
                          weight: 'bold',
                          size: 11
                        },
                        formatter: (value: number) => {
                          // Format with decimals if needed
                          return new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: primaryCurrency,
                            minimumFractionDigits: value % 1 !== 0 ? 2 : 0,
                            maximumFractionDigits: 2
                          }).format(value);
                        },
                        textStrokeColor: (context: any) => {
                          // Use the slice color as stroke for better visibility
                          const backgroundColor = context.dataset.backgroundColor[context.dataIndex];
                          return backgroundColor || '#000000';
                        },
                        textStrokeWidth: 2,
                        padding: 4,
                        backgroundColor: (context: any) => {
                          // Use the slice color as background with some opacity
                          const backgroundColor = context.dataset.backgroundColor[context.dataIndex];
                          return backgroundColor || 'rgba(0, 0, 0, 0.7)';
                        },
                        borderRadius: 4,
                        borderWidth: 2,
                        borderColor: '#ffffff',
                        clamp: true,
                        clip: false,
                        offset: 8
                      }
                    }
                  }}
                />
              </div>
            ) : (
              <EmptyState message="No vendor expense data available yet." />
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold text-gray-900">Monthly Trend</h3>
          </div>
          <div className="card-body">
            {monthlyTrendData ? (
              <div className="h-64">
                <Line
                  data={monthlyTrendData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        display: false
                      },
                      datalabels: {
                        anchor: 'end',
                        align: 'top',
                        color: '#ffffff',
                        font: {
                          weight: 'bold',
                          size: 12
                        },
                        formatter: (value: number) => {
                          // Format with decimals if needed
                          return new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: primaryCurrency,
                            minimumFractionDigits: value % 1 !== 0 ? 2 : 0,
                            maximumFractionDigits: 2
                          }).format(value);
                        },
                        backgroundColor: PRIMARY_CHART_COLOR,
                        borderRadius: 4,
                        padding: 6,
                        borderWidth: 2,
                        borderColor: '#ffffff'
                      }
                    },
                    scales: {
                      y: {
                        ticks: {
                          callback: (value) => formatCurrency(Number(value), primaryCurrency)
                        }
                      }
                    }
                  }}
                />
              </div>
            ) : (
              <EmptyState message="No monthly trend data available yet." />
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-1 gap-6">
        <div className="card">
          <div className="card-header flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Invoice Aging</h3>
            <span className="text-xs text-gray-500">Click a bar to view the daily breakdown</span>
          </div>
          <div className="card-body">
            {lastSevenDaysData ? (
              <div className="h-72">
                <Bar
                  data={lastSevenDaysData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    onClick: handleDailyBarClick,
                    plugins: {
                      legend: {
                        display: false
                      },
                      tooltip: {
                        callbacks: {
                          label: (ctx) =>
                            `${ctx.dataset.label}: ${formatCurrency(Number(ctx.raw), primaryCurrency)}`
                        }
                      },
                      datalabels: {
                        anchor: 'center',
                        align: 'center',
                        color: '#ffffff',
                        font: {
                          weight: 'bold',
                          size: 13
                        },
                        formatter: (value: number) => {
                          // Format with decimals if needed
                          return new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: primaryCurrency,
                            minimumFractionDigits: value % 1 !== 0 ? 2 : 0,
                            maximumFractionDigits: 2
                          }).format(value);
                        },
                        display: () => {
                          // Always show labels
                          return true;
                        }
                      }
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        ticks: {
                          callback: (value) => formatCurrency(Number(value), primaryCurrency)
                        }
                      }
                    }
                  }}
                />
              </div>
            ) : (
              <EmptyState message="No recent activity available yet." />
            )}
          </div>
        </div>
      </div>

      {isFilterPanelOpen && (
        <div style={{ margin: '0px !important' }}>
        <div className="fixed inset-0 z-50 flex">
          <div
            className="flex-1 bg-black/40"
            onClick={() => setIsFilterPanelOpen(false)}
            aria-hidden="true"
          />
          <div className="relative ml-auto flex h-full w-full max-w-md flex-col bg-white shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Filter Analytics</h3>
                <p className="text-xs text-gray-500">Refine the data shown across all charts.</p>
              </div>
              <button
                onClick={() => setIsFilterPanelOpen(false)}
                className="rounded-full p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                aria-label="Close filters"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
              <div>
                <h4 className="text-sm font-semibold text-gray-700">Date Range</h4>
                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <label className="text-xs font-medium text-gray-500">
                    Start Date
                    <input
                      type="date"
                      value={draftFilters.startDate ?? ''}
                      onChange={(event) =>
                        setDraftFilters((prev) => {
                          const nextStart = event.target.value ? event.target.value : null;
                          let nextEnd = prev.endDate;
                          if (nextStart && nextEnd && nextEnd <= nextStart) {
                            nextEnd = null;
                          }
                          return {
                            ...prev,
                            startDate: nextStart,
                            endDate: nextEnd
                          };
                        })
                      }
                      className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    />
                  </label>
                  <label className="text-xs font-medium text-gray-500">
                    End Date
                    <input
                      type="date"
                      value={draftFilters.endDate ?? ''}
                      min={draftFilters.startDate ? getNextIsoDate(draftFilters.startDate) : undefined}
                      onChange={(event) =>
                        setDraftFilters((prev) => {
                          const rawValue = event.target.value ? event.target.value : null;
                          if (rawValue && prev.startDate && rawValue <= prev.startDate) {
                            return prev;
                          }
                          return {
                            ...prev,
                            endDate: rawValue
                          };
                        })
                      }
                      className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    />
                  </label>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-gray-700">Departments</h4>
                  {optionsLoading && (
                    <span className="text-xs text-gray-400">Loading options…</span>
                  )}
                </div>
                <div className="mt-3 max-h-40 space-y-2 overflow-y-auto rounded-md border border-gray-100 p-3">
                  <div className="mb-3">
                    <input
                      type="search"
                      placeholder="Search departments"
                      value={departmentSearch}
                      onChange={(event) => setDepartmentSearch(event.target.value)}
                      className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    />
                  </div>
                  {filteredDepartments.length === 0 ? (
                    <p className="text-xs text-gray-400">
                      {departments.length === 0 ? 'No departments available.' : 'No departments match your search.'}
                    </p>
                  ) : (
                    filteredDepartments.map((dept) => {
                      const id = Number(dept.id);
                      const checked = draftFilters.departmentIds.includes(id);
                      return (
                        <label key={dept.id} className="flex items-center gap-2 text-sm text-gray-700">
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            checked={checked}
                            onChange={(event) =>
                              setDraftFilters((prev) => ({
                                ...prev,
                                departmentIds: event.target.checked
                                  ? [...prev.departmentIds, id]
                                  : prev.departmentIds.filter((deptId) => deptId !== id)
                              }))
                            }
                          />
                          <span>{getDepartmentLabel(id)}</span>
                        </label>
                      );
                    })
                  )}
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-gray-700">Vendors</h4>
                <div className="mt-3 max-h-40 space-y-2 overflow-y-auto rounded-md border border-gray-100 p-3">
                  <div className="mb-3">
                    <input
                      type="search"
                      placeholder="Search vendors"
                      value={vendorSearch}
                      onChange={(event) => setVendorSearch(event.target.value)}
                      className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    />
                  </div>
                  {filteredVendors.length === 0 ? (
                    <p className="text-xs text-gray-400">
                      {vendors.length === 0 ? 'No vendors available.' : 'No vendors match your search.'}
                    </p>
                  ) : (
                    filteredVendors.map((vendor) => {
                      const id = Number(vendor.id);
                      const checked = draftFilters.vendorIds.includes(id);
                      return (
                        <label key={vendor.id} className="flex items-center gap-2 text-sm text-gray-700">
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            checked={checked}
                            onChange={(event) =>
                              setDraftFilters((prev) => ({
                                ...prev,
                                vendorIds: event.target.checked
                                  ? [...prev.vendorIds, id]
                                  : prev.vendorIds.filter((vendorId) => vendorId !== id)
                              }))
                            }
                          />
                          <span>{getVendorLabel(id)}</span>
                        </label>
                      );
                    })
                  )}
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-gray-700">Currencies</h4>
                <div className="mt-3 max-h-40 space-y-2 overflow-y-auto rounded-md border border-gray-100 p-3">
                  <div className="mb-3">
                    <input
                      type="search"
                      placeholder="Search currencies"
                      value={currencySearch}
                      onChange={(event) => setCurrencySearch(event.target.value)}
                      className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    />
                  </div>
                  {filteredCurrencies.length === 0 ? (
                    <p className="text-xs text-gray-400">
                      {currencyOptions.length === 0
                        ? 'No currencies available.'
                        : 'No currencies match your search.'}
                    </p>
                  ) : (
                    filteredCurrencies.map((currency) => {
                      const code = currency.name?.toUpperCase() ?? '';
                      if (!code) {
                        return null;
                      }
                      const checked = draftFilters.currencies.includes(code);
                      return (
                        <label key={currency.id ?? code} className="flex items-center gap-2 text-sm text-gray-700">
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            checked={checked}
                            onChange={(event) =>
                              setDraftFilters((prev) => ({
                                ...prev,
                                currencies: event.target.checked
                                  ? code && !prev.currencies.includes(code)
                                    ? [...prev.currencies, code]
                                    : prev.currencies
                                  : prev.currencies.filter((value) => value !== code)
                              }))
                            }
                          />
                          <span className={getCurrencyFontClass(code)}>{code}</span>
                        </label>
                      );
                    })
                  )}
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-gray-700">Statuses</h4>
                <div className="mt-3 grid grid-cols-1 gap-2">
                  {STATUS_OPTIONS.map((status) => {
                    const checked = draftFilters.statuses.includes(status.value);
                    return (
                      <label key={status.value} className="flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          checked={checked}
                          onChange={(event) =>
                            setDraftFilters((prev) => ({
                              ...prev,
                              statuses: event.target.checked
                                ? [...prev.statuses, status.value]
                                : prev.statuses.filter((value) => value !== status.value)
                            }))
                          }
                        />
                        <span>{status.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-gray-700">Amount Range</h4>
                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <label className="text-xs font-medium text-gray-500">
                    Min Amount
                    <input
                      type="number"
                      min="0"
                      value={draftFilters.minAmount}
                      onChange={(event) =>
                        setDraftFilters((prev) => ({
                          ...prev,
                          minAmount: event.target.value
                        }))
                      }
                      className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    />
                  </label>
                  <label className="text-xs font-medium text-gray-500">
                    Max Amount
                    <input
                      type="number"
                      min="0"
                      value={draftFilters.maxAmount}
                      onChange={(event) =>
                        setDraftFilters((prev) => ({
                          ...prev,
                          maxAmount: event.target.value
                        }))
                      }
                      className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                    />
                  </label>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-gray-700">Monthly Trend Window</h4>
                <div className="mt-3 flex items-center gap-3">
                  <input
                    type="number"
                    min={1}
                    max={36}
                    value={draftFilters.maxMonths}
                    onChange={(event) => {
                      const value = event.target.value;
                      setDraftFilters((prev) => ({
                        ...prev,
                        maxMonths: value === '' ? DEFAULT_MAX_MONTHS : clampMonths(Number(value))
                      }));
                    }}
                    className="w-24 rounded-md border border-gray-200 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                  <span className="text-xs text-gray-400">
                    Months shown in the monthly trend chart
                  </span>
                </div>
              </div>
            </div>

            <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-between">
              <button
                type="button"
                onClick={handleResetDraftFilters}
                className="text-sm font-medium text-gray-500 hover:text-gray-700"
              >
                Reset
              </button>
              <button
                type="button"
                onClick={handleApplyFilters}
                disabled={!filtersChanged || loading}
                className={`inline-flex items-center rounded-md px-4 py-2 text-sm font-semibold ${filtersChanged && !loading
                  ? 'bg-primary-600 text-white hover:bg-primary-700'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
        </div>
      )}

      {dailyModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm p-4">
          <div className="w-full max-w-5xl bg-white rounded-lg shadow-xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Daily Analytics — {selectedDate}
                </h3>
                <p className="text-sm text-gray-500">
                  Summary of invoice activity and status movements for the selected day.
                </p>
              </div>
              <button
                onClick={() => setDailyModalOpen(false)}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                aria-label="Close daily analytics"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            <div className="max-h-[80vh] overflow-y-auto px-6 py-4 space-y-6">
              {dailyLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
                </div>
              ) : dailyError ? (
                <div className="rounded-lg bg-red-50 p-4 border border-red-200 text-sm text-red-700">
                  {dailyError}
                </div>
              ) : dailyData ? (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="rounded-lg border border-gray-200 p-4">
                      <p className="text-sm text-gray-500">Invoices Created</p>
                      <p className="mt-2 text-2xl font-semibold text-gray-900">
                        {Number(dailyData.summary?.invoice_count || 0).toLocaleString()}
                      </p>
                    </div>
                    <div className="rounded-lg border border-gray-200 p-4">
                      <p className="text-sm text-gray-500">Total Amount</p>
                      <p className={`mt-2 text-2xl font-semibold text-gray-900 ${dailyCurrencyClass}`}>
                        {formatCurrency(Number(dailyData.summary?.total_amount || 0), dailyCurrency)}
                      </p>
                    </div>
                  </div>

                  <section>
                    <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                      Status Breakdown
                    </h4>
                    {dailyData.statusBreakdown.length ? (
                      <div className="overflow-x-auto border border-gray-200 rounded-lg">
                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left font-medium text-gray-600 uppercase tracking-wider">
                                Status
                              </th>
                              <th className="px-4 py-3 text-right font-medium text-gray-600 uppercase tracking-wider">
                                Count
                              </th>
                              <th className="px-4 py-3 text-right font-medium text-gray-600 uppercase tracking-wider">
                                Amount
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-100">
                            {dailyData.statusBreakdown.map((row) => (
                              <tr key={`${row.status || 'unknown'}`}>
                                <td className="px-4 py-3 text-gray-700 capitalize">
                                  {row.status || 'Unknown'}
                                </td>
                                <td className="px-4 py-3 text-right text-gray-900">
                                  {Number(row.status_count || 0).toLocaleString()}
                                </td>
                                <td className={`px-4 py-3 text-right text-gray-900 ${dailyCurrencyClass}`}>
                                  {formatCurrency(Number(row.total_amount || 0), dailyCurrency)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <EmptyState message="No status distribution found for this day." small />
                    )}
                  </section>

                  <section>
                    <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                      Invoices
                    </h4>
                    {dailyData.invoices.length ? (
                      <div className="overflow-x-auto border border-gray-200 rounded-lg">
                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left font-medium text-gray-600 uppercase tracking-wider">
                                Invoice #
                              </th>
                              <th className="px-4 py-3 text-left font-medium text-gray-600 uppercase tracking-wider">
                                Vendor
                              </th>
                              <th className="px-4 py-3 text-left font-medium text-gray-600 uppercase tracking-wider">
                                Department
                              </th>
                              <th className="px-4 py-3 text-left font-medium text-gray-600 uppercase tracking-wider">
                                Status
                              </th>
                              <th className="px-4 py-3 text-right font-medium text-gray-600 uppercase tracking-wider">
                                Amount
                              </th>
                              <th className="px-4 py-3 text-left font-medium text-gray-600 uppercase tracking-wider">
                                Created
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-100">
                            {dailyData.invoices.map((invoice) => (
                              <tr key={invoice.id}>
                                <td className="px-4 py-3 text-gray-900 font-medium">
                                  {invoice.invoice_number || `#${invoice.id}`}
                                </td>
                                <td className="px-4 py-3 text-gray-700">
                                  {invoice.vendor_name || 'Unknown Vendor'}
                                </td>
                                <td className="px-4 py-3 text-gray-700">
                                  {invoice.department_name || 'Unassigned'}
                                </td>
                                <td className="px-4 py-3 text-gray-700 capitalize">
                                  {invoice.status || 'unknown'}
                                </td>
                                <td className={`px-4 py-3 text-right text-gray-900 ${dailyCurrencyClass}`}>
                                  {formatCurrency(Number(invoice.total_amount || 0), dailyCurrency)}
                                </td>
                                <td className="px-4 py-3 text-gray-500">
                                  {invoice.created_at
                                    ? new Date(invoice.created_at).toLocaleString()
                                    : '—'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <EmptyState message="No invoices were created on this day." small />
                    )}
                  </section>
                </>
              ) : (
                <EmptyState message="No data found for the selected day." />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const EmptyState: React.FC<{ message: string; small?: boolean }> = ({ message, small }) => (
  <div
    className={`flex flex-col items-center justify-center text-center text-gray-400 ${small ? 'py-6 text-xs' : 'py-10 text-sm'
      }`}
  >
    <AlertCircle className={`mb-2 ${small ? 'h-4 w-4' : 'h-6 w-6'}`} />
    <p>{message}</p>
  </div>
);

export default Dashboard; 